import React, { useCallback, useReducer, useState } from "react";
import { commitMutation, graphql } from "react-relay";

import * as Sentry from "@sentry/browser";
import cx from "classnames";
import uuidv4 from "uuid/v4";

import { AppContext } from "~/common";
import { useMedia } from "~/lib/hooks";
import { hasOwnProperty } from "~/lib/js";
import { promptHeadingsEnabled } from "~/lib/prose/plugins/prompt";
import { commitMutationPromise, useLazyLoadQuery } from "~/lib/relayUtil";
import { Fallbacks } from "~/lib/suspense";
import { isTeams } from "~/lib/utils";
import { environment } from "~/models";
import { useLibraryNavigationFeatureFlagOn } from "~/nav/featureFlag";
import { Copy, Create, Edit, useNoteTemplateModal } from "~/noteTemplate/modal";
import { SmartTemplateAuthor } from "~/noteTemplate/TemplateAuthor";
import { Button, toast } from "~/ui";
import { colors } from "~/ui/colors";
import { HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";
import { ButtonLink } from "~/ui/Link";
import LoadingSpinner from "~/ui/LoadingSpinner";
import { useModals } from "~/ui/modals";

import ActionItemIntegrations from "./ActionItemIntegrations";
import { Automation, ConfigPage } from "./Automations";
import { AUTO_SHARE_TO_CHANNELS_AUTOMATION } from "./Automations/AutoShareToChannels";
import { slackScopesAreValid } from "./Automations/Slack";
import { StreamRecordingDiscloure } from "./Automations/StreamRecordingDisclosure";
import { AUTOMATION_CODE, type AutomationType, type ConfigurationType } from "./Automations/types";
import { AutoShareRecapSettings } from "./AutoShareRecapSettings";
import { Breadcrumbs } from "./Breadcrumbs";
import { CalendarEvents } from "./CalendarEvents";
import { CarryForwardActionItem, CarryForwardTalkingPoint } from "./CarryForward";
import Details from "./Details";
import { MeetingFeedbackPrompt, MeetingFeedbackTab } from "./MeetingFeedback";
import { ObjectivesPage } from "./Objectives";
import Tabs from "./Tabs/TabsSettings";

import type { StreamSettings_EnsurePromptNotificationAutomationMutation as EnsurePromptNotificationAutomationMutation } from "~/__generated__/StreamSettings_EnsurePromptNotificationAutomationMutation.graphql";
import type { StreamSettings_ToggleSchedulingMeetingAssistantMutation as ToggleSchedulingMeetingAssistantMutation } from "~/__generated__/StreamSettings_ToggleSchedulingMeetingAssistantMutation.graphql";
import type { StreamSettingsModalQuery } from "~/__generated__/StreamSettingsModalQuery.graphql";
import type { StreamSettingsMutation } from "~/__generated__/StreamSettingsMutation.graphql";
import type {
	MeetingFeedbackSettingChoices,
	StreamSettingsSetMeetingFeedbackMutation,
} from "~/__generated__/StreamSettingsSetMeetingFeedbackMutation.graphql";
import type { StreamSettingsSetTemplateMutation } from "~/__generated__/StreamSettingsSetTemplateMutation.graphql";

import styles from "./StreamSettings.module.css";

export { renameStreamPrompt } from "./Details";

const AUTOMATION_CODES_FOR_NOTES_SECTION = [
	AUTOMATION_CODE.TIME_SAVER,
	AUTOMATION_CODE.MEETING_REMINDER,
	AUTOMATION_CODE.MEETING_RECAP,
	AUTOMATION_CODE.PROMPT_NOTIFICATION,
];

export type StreamSettingsResponseStream = Extract<
	StreamSettingsModalQuery["response"]["stream"],
	{ __typename: "NoteStream" }
>;

export type StreamSettingsViewer = StreamSettingsModalQuery["response"]["viewer"];

export const SectionTitles = {
	template: "Template",
	"carry-forward": "Carry-forward",
	"calendar-events": "Calendar events",
	"meeting-feedback": "Meeting feedback",
	objectives: "Objectives",
	integrations: "Integrations",
	automations: "Automations",
	details: "Details",
	tabs: "Tabs and bookmarks",
} as const;

export type SectionKey = keyof typeof SectionTitles;

export type NavAction =
	| { type: "back" }
	| { type: "forward"; payload: SectionKey }
	| { type: "to"; payload: SectionKey[] };

const setMeetingFeedbackSettingMutation = graphql`
	mutation StreamSettingsSetMeetingFeedbackMutation(
		$streamId: ID!
		$setting: MeetingFeedbackSettingChoices!
		$value: Boolean!
	) {
		noteStreamSetMeetingFeedbackSetting(streamId: $streamId, setting: $setting, value: $value) {
			stream {
				isMeetingFeedbackTabEnabled
				isMeetingFeedbackPromptEnabled
				isMeetingFeedbackAnonymous

				tabs {
					...TabsAndBookmarks_noteStreamTab @relay(mask: false)
				}
			}
		}
	}
`;

function setMeetingFeedbackSetting(streamId: string, setting: MeetingFeedbackSettingChoices) {
	return (e: React.FormEvent<HTMLInputElement>, { checked }: { checked?: boolean }) => {
		commitMutation<StreamSettingsSetMeetingFeedbackMutation>(environment, {
			mutation: setMeetingFeedbackSettingMutation,
			variables: {
				streamId,
				setting,
				value: checked ?? false,
			},

			onCompleted: ({ noteStreamSetMeetingFeedbackSetting }, err) => {
				if (err?.length || !noteStreamSetMeetingFeedbackSetting) throw new Error("Failed to save settings");
			},
		});
	};
}

export function setSchedulingMeetingAssistantConfigValue(streamId: string, value: boolean): Promise<boolean> {
	return new Promise((resolve, reject) => {
		commitMutation<ToggleSchedulingMeetingAssistantMutation>(environment, {
			mutation: graphql`
				mutation StreamSettings_ToggleSchedulingMeetingAssistantMutation($streamId: ID!, $value: Boolean!) {
					toggleSchedulingMeetingAssistantAutomation(streamId: $streamId, value: $value) {
						stream {
							id
							schedulingMeetingAssistantAutomationIsActive
							automationConfigurations {
								...AutomationsConfigurationsData @relay(mask: false)
							}
						}
					}
				}
			`,
			variables: { streamId, value },
			onCompleted: ({ toggleSchedulingMeetingAssistantAutomation }, err) => {
				if (err?.length || !toggleSchedulingMeetingAssistantAutomation) {
					toast.error(err?.[0]?.message || "Failed to save automation");
					reject(err || new Error("Failed to save automation"));
				} else {
					resolve(toggleSchedulingMeetingAssistantAutomation.stream.schedulingMeetingAssistantAutomationIsActive);
				}
			},
		});
	});
}

const setSettingMutation = graphql`
	mutation StreamSettingsMutation($streamId: ID!, $setting: String!, $value: String) {
		noteStreamSetting(streamId: $streamId, setting: $setting, value: $value) {
			stream {
				carryTalkingPoints
				carryActionItems
				carryActionItemsAsLinks
				carryForwardWithNestedBlocks
				objectivesTabEnabled
				objectivesFilterAssignee
				objectivesFilterStatus
				objectivesFilterCycle

				tabs {
					...TabsAndBookmarks_noteStreamTab @relay(mask: false)
				}
			}
		}
	}
`;

function setStreamSetting(streamId: string, setting: string) {
	return (value: string | null) => () => {
		commitMutation<StreamSettingsMutation>(environment, {
			mutation: setSettingMutation,
			variables: {
				streamId,
				setting,
				value,
			},

			onCompleted: ({ noteStreamSetting }, err) => {
				if (err?.length || !noteStreamSetting) throw new Error("Failed to save settings");
			},
		});
	};
}

const setTemplateMutation = graphql`
	mutation StreamSettingsSetTemplateMutation($streamId: ID!, $templateId: ID) {
		noteStreamSetTemplateSetting(streamId: $streamId, templateId: $templateId) {
			stream {
				template {
					id
					title
					summary
					viewerCanEdit
					proseDoc
					upgradedProseDoc
					tags
					visibility
					author {
						...TemplateAuthor_Author
					}
				}
			}
		}
	}
`;

export function setStreamTemplate(streamId: string, templateId: string | null) {
	return commitMutationPromise<StreamSettingsSetTemplateMutation>(environment, {
		mutation: setTemplateMutation,
		variables: { streamId, templateId },
	});
}

function ErrorFallback() {
	React.useEffect(() => {
		Sentry.captureException(new Error("StreamSettingsModal could not find Series"));
	}, []);

	return null;
}

function LoadingFallback() {
	return <LoadingSpinner dimmer delay={1000} />;
}

interface Props {
	close: () => void;
	streamId: string;
	defaultSection?: SectionKey | [section: "automations", automationCodeName: string];
	fetchKey: string;
}

function StreamSettingsModal({ close, streamId, defaultSection = "details", fetchKey }: Props) {
	const props = useLazyLoadQuery<StreamSettingsModalQuery>(
		graphql`
			query StreamSettingsModalQuery($streamId: ID!) {
				viewer {
					...StreamAutoRecordAutomationConfig_viewer
					...BillingIdentityFeaturesPaywall_viewer
					remainingMeetingRecordingCredits
					hasAdminAccess
					hasAccessToScheduledMeetingRecordings
					integrations {
						office365 {
							id
						}
						slack {
							scopes
						}
					}
					user {
						id
						email
						fullName
						isGuest
					}
					account {
						allowInviting
						guestUsersAllowInvite
						allowExternalInviting
						allowMeetingRecording

						billingAccount {
							hasAccessToPromptHeadings: hasAccessToFeature(feature: prompt_headings)
							hasAccessToMeetingRecorderFeature: hasAccessToFeature(feature: meeting_recorder)
							hasAccessToMeetingAutomations: hasAccessToFeature(feature: meeting_automations)
							hasAccessToObjectives: hasAccessToFeature(feature: objectives)
							subscriptionBillingCycleAnchor
						}

						integrations {
							zoomBot {
								isConnected
							}
						}
					}
				}

				automations {
					id
					name
					codeName
					description
				}

				stream: node(id: $streamId) {
					__typename
					... on NoteStream {
						id
						type
						isExternal
						displayName
						description
						carryTalkingPoints
						carryActionItems
						carryActionItemsAsLinks
						carryForwardWithNestedBlocks
						isMeetingFeedbackTabEnabled
						isMeetingFeedbackPromptEnabled
						isMeetingFeedbackAnonymous
						objectivesTabEnabled
						objectivesFilterAssignee
						objectivesFilterStatus
						objectivesFilterCycle
						openActionItemCount
						notestreamusers {
							id
							accessType
							user {
								id
								fullName
								email
								image
								isGuest
							}
						}
						template {
							id
							title
							summary
							viewerCanEdit
							proseDoc
							tags
							visibility
							schemaVersion
							author {
								...TemplateAuthor_Author
							}
						}

						...CalendarEvents_NoteStream

						automationConfigurations {
							...AutomationsConfigurationsData @relay(mask: false)
						}

						otherUser {
							id
							fullName
							image
							relationshipToViewer
							isTopUser
						}

						tabs {
							...TabsAndBookmarks_noteStreamTab @relay(mask: false)
						}
					}
				}
			}
		`,
		{ streamId },
		// Using network-only doesn't actually cause the modal to fetch every time
		// it's open for some reason, presumably due to the underlying issue here:
		// https://github.com/facebook/relay/issues/3395
		//
		// To force it to refetch, we are using fetchKey, passed in and stored in
		// the parent component (see StreamSettingsModalWrapper below). Since the
		// stream settings query is a bit slow, we're using store-and-network to
		// give the illusion of better performance when re-opening the modal for
		// the same stream.
		{ fetchPolicy: "store-and-network", fetchKey },
	);

	let stream = props?.stream?.__typename === "NoteStream" ? props.stream : null;

	const isMobile = useMedia("(max-width: 735px)");
	const [pageStack, navigate] = useReducer(
		(state: SectionKey[], action: NavAction) => {
			switch (action.type) {
				case "back":
					return state.slice(0, -1);
				case "forward":
					return [...state, action.payload];
				case "to":
					return action.payload;
				default:
					return state;
			}
		},
		[
			isTeams && defaultSection === "template"
				? "carry-forward"
				: Array.isArray(defaultSection)
					? defaultSection[0]
					: defaultSection,
		],
	);
	const currentPage = pageStack[pageStack.length - 1];

	const toTabsSubpage = useCallback(
		(s: "meeting-feedback" | "objectives") => navigate({ type: "forward", payload: s }),
		[navigate],
	);

	const [automationConfig, setAutomationConfig] = useState<string | null>(
		Array.isArray(defaultSection) && defaultSection[1] !== "MEETING_RECAP_AUTO_SHARE" ? defaultSection[1] : null,
	); // automation code names
	const [showMeetingRecapAutoShareConfig, setShowMeetingRecapAutoShareConfig] = useState(
		defaultSection[1] === "MEETING_RECAP_AUTO_SHARE",
	);
	const openMeetingRecapAutoShareConfig = React.useCallback(() => {
		setShowMeetingRecapAutoShareConfig(true);
	}, [setShowMeetingRecapAutoShareConfig]);
	const clearAutomationConfig = useCallback(() => {
		setAutomationConfig(null);
		setShowMeetingRecapAutoShareConfig(false);
	}, [setShowMeetingRecapAutoShareConfig]);

	const automationConfigs: { [key: string]: ConfigurationType } = {};
	for (const config of stream?.automationConfigurations ?? []) {
		automationConfigs[config.automation.codeName] = config;
	}

	const automations: { [key: string]: AutomationType } = {};
	for (const automation of props.automations) {
		automations[automation.codeName] = automation;
	}

	const libraryNavigationFeatureFlagOn = useLibraryNavigationFeatureFlagOn();

	if (libraryNavigationFeatureFlagOn) {
		// We need to add this manually because this automation is not coming from the Automation DB model
		automations[AUTOMATION_CODE.AUTO_SHARE_TO_CHANNELS] = AUTO_SHARE_TO_CHANNELS_AUTOMATION;
	}

	const slacks = props.viewer.integrations.slack;
	const viewerSlackScopes = (slacks && slacks[0]?.scopes) ?? "";
	const viewerHasSlack = slackScopesAreValid(viewerSlackScopes);
	const { hasAccessToPromptHeadings } = props.viewer.account.billingAccount;

	const selectedTemplate = stream?.template;
	const selectedTemplateId = selectedTemplate?.id;
	const openTemplateModal = useNoteTemplateModal();
	const browseTemplates = useCallback(() => {
		openTemplateModal({
			noteId: "dummyFixme", // This is actually used to enable buttons in the template grid -- its value doesn't matter.
			mode: "browse",
			actionText: "Select",
			defaultSelected: selectedTemplateId,
		}).then(selected => {
			if (selected) {
				setStreamTemplate(streamId, selected);
			}
		});
	}, [openTemplateModal, selectedTemplateId, streamId]);
	const previewTemplate = useCallback(() => {
		if (!selectedTemplateId) return;
		openTemplateModal({
			noteId: "dummyFixme", // This is actually used to enable buttons in the template grid -- its value doesn't matter.
			mode: "preview",
			actionText: "Select",
			defaultPreviewing: selectedTemplateId,
			defaultSelected: selectedTemplateId,
		}).then(selected => {
			if (selected) {
				setStreamTemplate(streamId, selected);
			}
		});
	}, [streamId, openTemplateModal, selectedTemplateId]);
	const removeTemplate = useCallback(() => {
		setStreamTemplate(streamId, null);
	}, [streamId]);

	const modals = useModals();
	const editTemplate = useCallback(() => {
		if (!selectedTemplateId) throw new Error("Tried to edit nonexistent template");
		modals.open(({ close }) => <Edit close={close} templateId={selectedTemplateId} />);
	}, [modals, selectedTemplateId]);
	const createTemplate = useCallback(() => {
		modals
			.open<string>(({ close }) => (
				<Create
					schemaVersion={window.CURRENT_NOTES_SCHEMA_VERSION}
					close={close}
					hasAccessToPromptHeadings={hasAccessToPromptHeadings}
				/>
			))
			.then(createdId => {
				if (createdId != null) setStreamTemplate(streamId, createdId);
			});
	}, [modals, streamId, hasAccessToPromptHeadings]);

	const setTemplate = useCallback(
		(createdId: string) => async () => {
			const result = await setStreamTemplate(streamId, createdId);

			const newTemplate = result.noteStreamSetTemplateSetting?.stream.template;
			if (!newTemplate) {
				toast.error("Could not set as default template. Try browsing for the template instead.");
			} else {
				toast.success(`Series template has been set to “${newTemplate.title}”.`, {
					title: "Series template set",
				});
			}
		},
		[streamId],
	);

	const copyTemplate = useCallback(async () => {
		const parent = selectedTemplate;
		if (!parent) throw new Error("Tried to copy nonexistent template");

		const createdId = await modals.open<string>(({ close }) => <Copy close={close} templateId={parent.id} />);
		if (createdId === null) return;

		toast.success("Use this template for new notes created in this series?", {
			title: "Default series template",
			autoClose: 20000,
			closeButton: true,
			actions: (
				<Button onClick={setTemplate(createdId)} variant="secondary">
					Set template
				</Button>
			),
		});
	}, [modals, selectedTemplate, setTemplate]);

	const havePromptNotificationConfig = hasOwnProperty(automationConfigs, AUTOMATION_CODE.PROMPT_NOTIFICATION);
	const haveSchedulingMeetingAssistantConfig = hasOwnProperty(
		automationConfigs,
		AUTOMATION_CODE.SCHEDULING_MEETING_ASSISTANT,
	);

	const onAutomationConfigure = React.useCallback(
		(codeName: string) => {
			if (codeName === AUTOMATION_CODE.SCHEDULING_MEETING_ASSISTANT) {
				// This is called when user toggles the automation on but there's no configurationId
				// See onChange in Automation component (StreamSettings/Automations/index.tsx)
				if (!haveSchedulingMeetingAssistantConfig) {
					setSchedulingMeetingAssistantConfigValue(streamId, true);
				}

				return;
			}

			if (codeName === AUTOMATION_CODE.PROMPT_NOTIFICATION) {
				if (!promptHeadingsEnabled) return;

				if (!havePromptNotificationConfig) {
					commitMutation<EnsurePromptNotificationAutomationMutation>(environment, {
						mutation: graphql`
							mutation StreamSettings_EnsurePromptNotificationAutomationMutation($streamId: ID!) {
								NoteStream_EnsurePromptNotificationAutomation(streamId: $streamId) {
									stream {
										automationConfigurations {
											...AutomationsConfigurationsData @relay(mask: false)
										}
									}
								}
							}
						`,
						variables: {
							streamId,
						},
						onCompleted() {
							setAutomationConfig(codeName);
						},
					});
				}
			}
			setAutomationConfig(codeName);
		},
		[streamId, havePromptNotificationConfig, haveSchedulingMeetingAssistantConfig],
	);

	// UX Simplification
	const appContext = React.useContext(AppContext);
	const uxSimplificationEnabled = appContext?.user?.uxSimplificationEnabled;

	const SectionNavButton: React.FC<{ section: SectionKey }> = ({ section }) => (
		<button
			type="button"
			onClick={useCallback(() => navigate({ type: "to", payload: [section] }), [section])}
			className={cx(styles.sectionNavButton, { [styles.current]: pageStack.includes(section) })}
		>
			{SectionTitles[section]}
		</button>
	);

	if (stream?.isExternal) return null;

	return (
		<Modal onClose={close} data-test-id="stream-settings-modal" width="41.25rem">
			<Modal.Header>
				<Modal.Title>Settings</Modal.Title>
				<Modal.HeaderActions onClose={close}>
					<HelpCenterIcon articleId="4892111" title="Settings" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<nav className={styles.sectionNav}>
					<SectionNavButton section="details" />
					{stream?.type !== "goalset" && <SectionNavButton section="automations" />}
					{!isTeams && <SectionNavButton section="template" />}
					{!uxSimplificationEnabled && <SectionNavButton section="tabs" />}
					<SectionNavButton section="carry-forward" />
					{stream?.type !== "goalset" && <SectionNavButton section="calendar-events" />}
					<SectionNavButton section="integrations" />
				</nav>
				<div
					className={cx(styles.sectionBody, {
						[styles.objectivesBody]: currentPage === "objectives",
						[styles.integrationsBody]: currentPage === "integrations",
					})}
				>
					<Breadcrumbs pageStack={pageStack} navigate={navigate} />
					{!stream ? (
						"Error: Could not find note series. It may have been deleted. Please try again later."
					) : currentPage === "template" ? (
						<>
							<p className={styles.infoText}>
								The selected template will be applied to all new notes created in this series.
							</p>
							<section className={styles.settingCard}>
								{selectedTemplate ? (
									<>
										<main
											onClick={previewTemplate}
											tabIndex={0}
											role="button"
											aria-label="Preview template"
											className={styles.clickable}
										>
											<SmartTemplateAuthor author={selectedTemplate.author} />
											<h1 data-test-id="selected-template-title">{selectedTemplate.title}</h1>
											{selectedTemplate.summary && <p>{selectedTemplate.summary}</p>}
										</main>
										<footer>
											<Button variant="secondary" size="normal" onClick={removeTemplate}>
												Remove
											</Button>
											<Button variant="secondary" size="normal" onClick={browseTemplates}>
												Browse templates…
											</Button>
										</footer>
									</>
								) : (
									<>
										<main>
											<p className={styles.emptyStateText}>No template selected</p>
										</main>
										<footer>
											<Button variant="primary" size="normal" onClick={browseTemplates}>
												Browse templates…
											</Button>
										</footer>
									</>
								)}
							</section>
							{!isMobile && (
								<p className={styles.infoTextSmall}>
									{selectedTemplate ? (
										selectedTemplate.viewerCanEdit ? (
											<ButtonLink onClick={editTemplate} color={colors.royal[700]}>
												Edit this template
											</ButtonLink>
										) : (
											<>
												To edit this {selectedTemplate.visibility === "WORKSPACE" ? "Workspace" : null} template,{" "}
												<ButtonLink onClick={copyTemplate} color={colors.royal[700]}>
													copy to My templates
												</ButtonLink>
											</>
										)
									) : (
										<ButtonLink onClick={createTemplate} color={colors.royal[700]}>
											Create my own template
										</ButtonLink>
									)}
								</p>
							)}
						</>
					) : currentPage === "carry-forward" ? (
						<main>
							<CarryForwardTalkingPoint
								title="talking points"
								value={stream.carryTalkingPoints ?? null}
								onSetValue={setStreamSetting(streamId, "carry_talking_points")}
							/>
							<CarryForwardActionItem
								title="action items"
								value={stream.carryActionItems ?? null}
								onSetValue={setStreamSetting(streamId, "carry_action_items")}
								carryActionItemsAsLinksValue={stream.carryActionItemsAsLinks}
								onSetCarryActionItemsAsLinksValue={setStreamSetting(streamId, "carry_action_items_as_links")}
								carryNestedBlocksValue={stream.carryForwardWithNestedBlocks}
								onSetCarryNestedBlocksValue={setStreamSetting(streamId, "carry_forward_with_nested_blocks")}
							/>
						</main>
					) : currentPage === "calendar-events" ? (
						<CalendarEvents streamKey={stream} />
					) : currentPage === "meeting-feedback" ? (
						<main>
							<MeetingFeedbackPrompt
								onChange={setMeetingFeedbackSetting(streamId, "PROMPT")}
								onAnonymousChange={setMeetingFeedbackSetting(streamId, "ANONYMOUS")}
								// value={stream.isMeetingFeedbackPromptEnabled ?? true}
								stream={stream}
								streamId={streamId}
								withBorder
								viewer={props.viewer}
							/>
							<MeetingFeedbackTab
								onChange={setMeetingFeedbackSetting(streamId, "TAB")}
								stream={stream}
								viewer={props.viewer}
							/>
						</main>
					) : currentPage === "objectives" ? (
						<ObjectivesPage data={stream} setSetting={setStreamSetting} />
					) : currentPage === "integrations" ? (
						<main>
							<ActionItemIntegrations stream={stream} />
						</main>
					) : currentPage === "automations" ? (
						<main>
							<React.Suspense fallback={<LoadingFallback />}>
								{automationConfig !== null && automations[automationConfig] ? (
									<ConfigPage
										streamId={streamId}
										automation={automations[automationConfig]}
										onBack={clearAutomationConfig}
										configuration={automationConfigs[automationConfig]}
										usersWithAccess={stream.notestreamusers.map(nsu => nsu.user) ?? []}
										viewerHasOffice={!!props.viewer.integrations.office365?.id}
										viewerHasSlack={viewerHasSlack}
										viewerHasZoom={!!props.viewer.account.integrations.zoomBot?.isConnected}
									/>
								) : showMeetingRecapAutoShareConfig ? (
									<ConfigPage
										streamId={streamId}
										automation={{ codeName: "MEETING_RECAP_AUTO_SHARE", id: "fake" }}
										onBack={clearAutomationConfig}
										usersWithAccess={stream.notestreamusers.map(nsu => nsu.user) ?? []}
										viewerHasOffice={!!props.viewer.integrations.office365?.id}
										viewerHasSlack={viewerHasSlack}
										viewerHasZoom={!!props.viewer.account.integrations.zoomBot?.isConnected}
									/>
								) : (
									<>
										{libraryNavigationFeatureFlagOn ? (
											<>
												<h3 className={styles.automationsSectionHeader}>Note Taker & Recap</h3>
												{props.viewer.hasAccessToScheduledMeetingRecordings &&
													automations[AUTOMATION_CODE.SCHEDULING_MEETING_ASSISTANT] && (
														<Automation
															streamId={streamId}
															key={automations[AUTOMATION_CODE.SCHEDULING_MEETING_ASSISTANT].id}
															automation={automations[AUTOMATION_CODE.SCHEDULING_MEETING_ASSISTANT]}
															config={automationConfigs[AUTOMATION_CODE.SCHEDULING_MEETING_ASSISTANT]}
															onConfigure={onAutomationConfigure}
															viewer={props.viewer}
														/>
													)}
												<React.Suspense fallback={<LoadingFallback />}>
													<AutoShareRecapSettings streamId={streamId} goToConfig={openMeetingRecapAutoShareConfig} />
												</React.Suspense>
												{automations[AUTOMATION_CODE.AUTO_SHARE_TO_CHANNELS] && (
													<Automation
														streamId={streamId}
														key={automations[AUTOMATION_CODE.AUTO_SHARE_TO_CHANNELS].id}
														automation={automations[AUTOMATION_CODE.AUTO_SHARE_TO_CHANNELS]}
														config={automationConfigs[AUTOMATION_CODE.AUTO_SHARE_TO_CHANNELS]}
														onConfigure={onAutomationConfigure}
														viewer={props.viewer}
													/>
												)}
												{props.viewer.hasAccessToScheduledMeetingRecordings && (
													<React.Suspense fallback={<LoadingFallback />}>
														<StreamRecordingDiscloure streamId={streamId} />
													</React.Suspense>
												)}
												<h3 className={styles.automationsSectionHeader}>Notes</h3>
												{AUTOMATION_CODES_FOR_NOTES_SECTION.map(codeName => {
													return (
														automations[codeName] && (
															<Automation
																streamId={streamId}
																key={automations[codeName].id}
																automation={automations[codeName]}
																config={automationConfigs[codeName]}
																onConfigure={onAutomationConfigure}
																viewer={props.viewer}
															/>
														)
													);
												})}
											</>
										) : (
											props.automations.map(automation => {
												let extra: JSX.Element | null = null;

												if (automation.codeName === AUTOMATION_CODE.SCHEDULING_MEETING_ASSISTANT) {
													extra = (
														<React.Suspense fallback={<LoadingFallback />}>
															<AutoShareRecapSettings
																streamId={streamId}
																goToConfig={openMeetingRecapAutoShareConfig}
															/>
														</React.Suspense>
													);
												}

												return (
													<>
														<Automation
															streamId={streamId}
															key={automation.id}
															automation={automation}
															config={automationConfigs[automation.codeName]}
															onConfigure={onAutomationConfigure}
															viewer={props.viewer}
														/>
														{extra}
													</>
												);
											})
										)}
									</>
								)}
							</React.Suspense>
						</main>
					) : currentPage === "details" ? (
						<Details streamId={stream.id ?? ""} title={stream.displayName ?? ""} user={stream.otherUser} />
					) : currentPage === "tabs" ? (
						<Tabs stream={stream} viewer={props.viewer} goToSection={toTabsSubpage} />
					) : null}
				</div>
			</Modal.Body>
		</Modal>
	);
}

export const useStreamSettingsModal = (streamId: string, defaultSection: Props["defaultSection"] = "details") => {
	const modals = useModals();
	const openModal = useCallback(
		() =>
			modals.open(props => (
				<StreamSettingsModalWrapper {...props} streamId={streamId} defaultSection={defaultSection} />
			)),
		[modals, streamId, defaultSection],
	);

	return openModal;
};

export default function StreamSettingsModalWrapper(props: Omit<Props, "fetchKey">) {
	// Fetch key needs to be part of this component's state since the
	// StreamSettingsModal component will be remounted (due to Suspense), which
	// causes an infinite fetch loop.
	const [fetchKey] = React.useState(uuidv4);

	return (
		<Fallbacks loading={<LoadingFallback />} error={<ErrorFallback />}>
			<StreamSettingsModal fetchKey={fetchKey} {...props} />
		</Fallbacks>
	);
}
